'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef } from 'react';
import { useWebHaptics } from 'web-haptics/react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { ArrowLeft } from 'lucide-react';
import { OutfitRegions } from './OutfitRegions';
import { CategoryDrawer } from './CategoryDrawer';
import { CircularQueueIndicator } from './CircularQueueIndicator';
import { Shimmer } from './Shimmer';

// Lazy load modal - only appears on user interaction
const SaveOutfitModal = dynamic(() => import('./SaveOutfitModal').then(mod => mod.SaveOutfitModal));
import { useCloset } from '@/contexts/ClosetContext';
import { transformClosetItem } from '@/lib/closet/transforms';
import { closetAuthHeaders, loginToCloset, readClosetAuthToken } from '@/lib/closet/client-auth';
import type { ClothingItem, ClothingCategory, Outfit, QueueItem, TryOnStage } from '@/types/closet';
import logger from '@/lib/logger';
interface OutfitBuilderProps {
  onSuccess: (outfit: Outfit) => void;
  onBack?: () => void;
  playSound: (type: 'hover' | 'click' | 'switch' | 'success') => void;
}
type SelectedItems = Partial<Record<ClothingCategory, ClothingItem>>;
type TryOnResult = {
  generatedImage: string;
  generatedOriginalImage?: string;
};
type TryOnLine = {
  type: 'stage';
  stage: TryOnStage;
  queuePosition?: number;
} | {
  type: 'result';
  generatedImage: string;
  generatedOriginalImage?: string;
} | {
  type: 'error';
  error: string;
};

// Hoisted to module scope so the `throw` lives outside the component body — the
// React Compiler bails on a ThrowStatement inside try/catch and would skip
// memoizing OutfitBuilder. Here it's plain async code the compiler never inspects.
async function requestVirtualTryon(body: {
  referenceImageUrl: string;
  clothingImageUrl?: string;
  clothingItemId: string;
  category?: string;
  subcategory?: string;
}, onStage: (stage: TryOnStage, queuePosition?: number) => void): Promise<TryOnResult> {
  const response = await fetch('/api/closet/virtual-tryon', {
    method: 'POST',
    headers: closetAuthHeaders({
      'Content-Type': 'application/json'
    }),
    body: JSON.stringify(body)
  });
  if (!response.ok || !response.body) {
    throw new Error('Virtual try-on failed');
  }

  // NDJSON: `stage` lines as the try-on moves along, then a `result` or
  // `error` line. A chunk can end mid-line, so hold the tail until the next.
  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  const readFrom = async (buffered: string): Promise<TryOnResult> => {
    const {
      value = '',
      done
    } = await reader.read();
    const lines = (buffered + value).split('\n');
    const rest = done ? '' : lines.pop() ?? '';
    for (const raw of lines) {
      if (!raw.trim()) continue;
      const line = JSON.parse(raw) as TryOnLine;
      if (line.type === 'stage') onStage(line.stage, line.queuePosition);else if (line.type === 'result') return line;else throw new Error(line.error);
    }
    if (done) throw new Error('Virtual try-on ended without a result');
    return readFrom(rest);
  };
  return readFrom('');
}
export function OutfitBuilder(props) {
  const $ = _c(6);
  let t0;
  if ($[0] !== props) {
    t0 = {
      ...props
    };
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const builder = useOutfitBuilderController(t0);
  if (builder.needsPassword) {
    let t1;
    if ($[2] !== builder) {
      t1 = <OutfitPasswordGate {...builder} />;
      $[2] = builder;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== builder) {
    t1 = <OutfitBuilderWorkspace {...builder} />;
    $[4] = builder;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function useOutfitBuilderController({
  onSuccess,
  onBack,
  playSound
}: OutfitBuilderProps) {
  const haptic = useWebHaptics();
  const [selectedItems, setSelectedItems] = useState<SelectedItems>({});
  const [currentImageUrl, setCurrentImageUrl] = useState<string>('/closet-base-image.png');
  const [currentOriginalImageUrl, setCurrentOriginalImageUrl] = useState<string>('/closet-base-image.png');
  const [processingQueue, setProcessingQueue] = useState<QueueItem[]>([]);
  const [currentProcessingIndex, setCurrentProcessingIndex] = useState(-1);
  const [activeCategory, setActiveCategory] = useState<ClothingCategory | null>(null);
  const [categoryItems, setCategoryItems] = useState<ClothingItem[]>([]);
  const {
    items: closetItems
  } = useCloset();
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [authState, setAuthState] = useState(() => {
    // Read the token during initialization so already-authenticated users never
    // see the password gate flash on mount. readClosetAuthToken is SSR-safe.
    const token = readClosetAuthToken();
    return token ? {
      isAuthenticated: true,
      showPasswordPrompt: false
    } : {
      isAuthenticated: false,
      showPasswordPrompt: true
    };
  });
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const isProcessingRef = useRef(false);
  const baseImageUrl = '/closet-base-image.png';
  const processingItemIds = processingQueue.reduce<string[]>((ids, queuedItem) => {
    if (queuedItem.status === 'processing') ids.push(queuedItem.item.id);
    return ids;
  }, []);
  const itemsByCategory = (() => {
    const categoryMap = new Map<ClothingCategory, ClothingItem[]>();
    for (const item of closetItems) {
      const transformed = transformClosetItem(item);
      const existing = categoryMap.get(transformed.category) || [];
      existing.push(transformed);
      categoryMap.set(transformed.category, existing);
    }
    return categoryMap;
  })();
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    if (!password) {
      setAuthError('PASSWORD IS REQUIRED');
      return;
    }
    try {
      if (await loginToCloset(password)) {
        setAuthState({
          isAuthenticated: true,
          showPasswordPrompt: false
        });
        setPassword('');
        haptic.trigger('success');
        playSound('success');
      } else {
        haptic.trigger('error');
        setAuthError('INCORRECT PASSWORD');
      }
    } catch {
      haptic.trigger('error');
      setAuthError('CONNECTION ERROR');
    }
  };
  const handleRegionClick = (category: ClothingCategory) => {
    playSound('click');
    setActiveCategory(() => category);
    setCategoryItems(itemsByCategory.get(category) || []);
  };
  const handleItemSelect = (item: ClothingItem) => {
    if (!activeCategory) return;
    playSound('click');

    // Add to queue
    setProcessingQueue(prev => [...prev, {
      item,
      category: activeCategory,
      status: 'pending'
    }]);

    // Don't close the drawer to allow quick multiple selections
  };

  // Clean up completed queue items after a delay
  useEffect(() => {
    const allCompleted = processingQueue.length > 0 && processingQueue.every(item => item.status === 'completed');
    if (allCompleted) {
      const timer = setTimeout(() => {
        setProcessingQueue([]);
      }, 2000); // Clear after 2 seconds

      return () => clearTimeout(timer);
    }
  }, [processingQueue]);

  // Queue processor: applies one pending item at a time, re-running whenever
  // the queue changes. `isProcessingRef` serialises the try-on calls (they
  // chain — each one's output image is the next one's input), and `ignore`
  // drops a response that lands after unmount.
  //
  // Every bail-out is checked *before* the ref is claimed; a `return` taken
  // after claiming it would leave it stuck true and deadlock the queue.
  useEffect(() => {
    if (isProcessingRef.current) return;
    const pendingIndex = processingQueue.findIndex(q => q.status === 'pending');
    const queueItem = pendingIndex === -1 ? undefined : processingQueue[pendingIndex];
    if (!queueItem) return;
    let ignore = false;
    isProcessingRef.current = true;
    setCurrentProcessingIndex(pendingIndex);
    setProcessingQueue(prev => prev.map((q, i) => i === pendingIndex ? {
      ...q,
      status: 'processing'
    } : q));
    const finish = () => {
      isProcessingRef.current = false;
      setCurrentProcessingIndex(-1);
    };
    requestVirtualTryon({
      referenceImageUrl: currentImageUrl,
      clothingImageUrl: queueItem.item.imageUrl,
      clothingItemId: queueItem.item.id,
      category: queueItem.category,
      subcategory: queueItem.item.subcategory
    }, (stage, queuePosition) => {
      if (ignore) return;
      setProcessingQueue(prev => prev.map((q, i) => i === pendingIndex ? {
        ...q,
        stage,
        queuePosition
      } : q));
    }).then(result => {
      if (ignore) return;
      setCurrentImageUrl(result.generatedImage);
      if (result.generatedOriginalImage) {
        setCurrentOriginalImageUrl(result.generatedOriginalImage);
      }
      setSelectedItems(prev => ({
        ...prev,
        // category is always set when the outfit builder enqueues an item
        [queueItem.category as ClothingCategory]: queueItem.item
      }));
      setProcessingQueue(prev => prev.map((q, i) => i === pendingIndex ? {
        ...q,
        status: 'completed'
      } : q));
      haptic.trigger('success');
      playSound('success');
      finish();
    }).catch(error => {
      if (ignore) return;
      haptic.trigger('error');
      logger.error('Failed to apply item:', error);
      // Remove failed item from queue
      setProcessingQueue(prev => prev.filter((_, i) => i !== pendingIndex));
      finish();
    });
    return () => {
      ignore = true;
      isProcessingRef.current = false;
    };
    // Re-runs on queue changes only; currentImageUrl is read at call time and
    // including it would restart an in-flight try-on with its own output.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processingQueue]);
  const handleSave = () => {
    const hasItems = Object.keys(selectedItems).length > 0;
    if (!hasItems) {
      return;
    }
    playSound('click');
    setShowSaveModal(true);
  };
  const handleReset = () => {
    playSound('click');
    setSelectedItems({});
    setCurrentImageUrl(baseImageUrl);
    setCurrentOriginalImageUrl(baseImageUrl);
    setProcessingQueue([]);
    setCurrentProcessingIndex(-1);
  };
  const hasSelectedItems = Object.keys(selectedItems).length > 0;
  return {
    needsPassword: authState.showPasswordPrompt && !authState.isAuthenticated,
    password,
    authError,
    onBack,
    playSound,
    currentImageUrl,
    processingQueue,
    currentProcessingIndex,
    selectedItems,
    activeCategory,
    categoryItems,
    processingItemIds,
    showSaveModal,
    currentOriginalImageUrl,
    hasSelectedItems,
    setPassword,
    setActiveCategory,
    setShowSaveModal,
    handlePasswordSubmit,
    handleRegionClick,
    handleItemSelect,
    handleReset,
    handleSave,
    onSuccess
  };
}
type OutfitBuilderState = ReturnType<typeof useOutfitBuilderController>;
function OutfitPasswordGate(t0) {
  const $ = _c(18);
  const {
    password,
    authError,
    onBack,
    setPassword,
    handlePasswordSubmit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h2 className="mb-6 text-sm">ENTER PASSWORD TO CREATE OUTFIT</h2>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== setPassword) {
    t2 = e => setPassword(e.target.value);
    $[1] = setPassword;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== password || $[4] !== t2) {
    t3 = <div><input type="password" aria-label="Password" placeholder="PASSWORD" value={password} onChange={t2} className="w-full border-b border-zinc-300 bg-transparent px-2 py-1 text-xs placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none dark:border-zinc-700 dark:placeholder:text-zinc-600 dark:focus:border-white" required={true} /></div>;
    $[3] = password;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== authError) {
    t4 = authError && <p className="text-xs text-red-600 dark:text-red-400">{authError}</p>;
    $[6] = authError;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <button type="submit" className="flex-1 border border-zinc-900 px-4 py-2 text-xs transition-colors hover:bg-zinc-900 hover:text-white dark:border-white dark:hover:bg-white dark:hover:text-zinc-900">CREATE OUTFIT</button>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== onBack) {
    t6 = onBack && <button type="button" onClick={onBack} className="flex-1 border border-zinc-300 px-4 py-2 text-xs transition-colors hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800">CANCEL</button>;
    $[9] = onBack;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <div className="flex gap-4 pt-4">{t5}{t6}</div>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== handlePasswordSubmit || $[14] !== t3 || $[15] !== t4 || $[16] !== t7) {
    t8 = <div className="flex size-full items-center justify-center bg-white dark:bg-neutral-950"><div className="relative w-full max-w-sm bg-white p-6 dark:bg-neutral-950">{t1}<form onSubmit={handlePasswordSubmit} className="space-y-4">{t3}{t4}{t7}</form></div></div>;
    $[13] = handlePasswordSubmit;
    $[14] = t3;
    $[15] = t4;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
function OutfitBuilderWorkspace(builder) {
  const $ = _c(11);
  let t0;
  if ($[0] !== builder.onBack) {
    t0 = <OutfitBuilderHeader onBack={builder.onBack} />;
    $[0] = builder.onBack;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  let t2;
  let t3;
  if ($[2] !== builder) {
    t1 = <OutfitPreview builder={builder} />;
    t2 = <OutfitBuilderActions builder={builder} />;
    t3 = <OutfitBuilderDrawers builder={builder} />;
    $[2] = builder;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <div className="flex h-full flex-col md:px-4">{t0}{t1}{t2}{t3}</div>;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function OutfitBuilderHeader(t0) {
  const $ = _c(4);
  const {
    onBack
  } = t0;
  let t1;
  if ($[0] !== onBack) {
    t1 = onBack && <button type="button" onClick={onBack} aria-label="Back to closet" className="flex items-center gap-2 text-xs opacity-60 transition-opacity hover:opacity-100"><ArrowLeft className="size-3" />BACK TO CLOSET</button>;
    $[0] = onBack;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="absolute top-0 right-0 left-0 z-10 flex items-center px-4 py-4 md:relative md:px-8 md:py-8">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function OutfitPreview(t0) {
  const $ = _c(16);
  const {
    builder
  } = t0;
  let t1;
  if ($[0] !== builder.currentImageUrl) {
    t1 = <Image src={builder.currentImageUrl} alt="Outfit preview" fill={true} sizes="(max-width: 768px) 100vw, 50vw" className="object-contain" priority={true} />;
    $[0] = builder.currentImageUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== builder.processingItemIds.length) {
    t2 = builder.processingItemIds.length > 0 && <Shimmer />;
    $[2] = builder.processingItemIds.length;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== builder.currentProcessingIndex || $[5] !== builder.processingQueue) {
    t3 = <CircularQueueIndicator queue={builder.processingQueue} currentProcessingIndex={builder.currentProcessingIndex} />;
    $[4] = builder.currentProcessingIndex;
    $[5] = builder.processingQueue;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== builder.handleRegionClick || $[8] !== builder.playSound || $[9] !== builder.selectedItems) {
    t4 = <OutfitRegions onRegionClick={builder.handleRegionClick} selectedItems={builder.selectedItems} disabled={false} playSound={builder.playSound} />;
    $[7] = builder.handleRegionClick;
    $[8] = builder.playSound;
    $[9] = builder.selectedItems;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t4) {
    t5 = <div className="flex flex-1 flex-col pb-20 md:items-center md:justify-center md:pb-0"><div className="flex flex-1 flex-col md:-mt-16 md:block md:w-full md:max-w-xl"><div className="relative flex-1 md:flex-none"><div className="absolute inset-0 overflow-hidden md:relative md:aspect-[3/4] md:w-full">{t1}{t2}{t3}{t4}</div></div></div></div>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}
function OutfitBuilderActions(t0) {
  const $ = _c(9);
  const {
    builder
  } = t0;
  const t1 = !builder.hasSelectedItems;
  let t2;
  if ($[0] !== builder.handleReset || $[1] !== t1) {
    t2 = <button type="button" onClick={builder.handleReset} disabled={t1} className="flex-1 border border-zinc-300 bg-neutral-950 px-4 py-2 text-xs text-white transition-colors duration-200 hover:bg-zinc-900 disabled:opacity-30 dark:border-zinc-700 dark:bg-neutral-950 dark:hover:bg-zinc-800">RESET</button>;
    $[0] = builder.handleReset;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = !builder.hasSelectedItems;
  let t4;
  if ($[3] !== builder.handleSave || $[4] !== t3) {
    t4 = <button type="button" onClick={builder.handleSave} disabled={t3} className="flex-1 border border-zinc-900 bg-white px-4 py-2 text-xs text-black transition-colors duration-200 hover:bg-zinc-100 disabled:opacity-30 dark:border-white dark:bg-white dark:text-black dark:hover:bg-zinc-200">SAVE OUTFIT</button>;
    $[3] = builder.handleSave;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t4) {
    t5 = <div className="fixed bottom-8 left-1/2 flex w-full max-w-xl -translate-x-1/2 gap-3 px-4">{t2}{t4}</div>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
function OutfitBuilderDrawers(t0) {
  const $ = _c(22);
  const {
    builder
  } = t0;
  const t1 = !!builder.activeCategory;
  let t2;
  if ($[0] !== builder) {
    t2 = () => builder.setActiveCategory(null);
    $[0] = builder;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = builder.activeCategory ? builder.selectedItems[builder.activeCategory] : undefined;
  let t4;
  if ($[2] !== builder.activeCategory || $[3] !== builder.categoryItems || $[4] !== builder.handleItemSelect || $[5] !== builder.processingItemIds || $[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <CategoryDrawer isOpen={t1} onClose={t2} category={builder.activeCategory} items={builder.categoryItems} loading={false} onItemSelect={builder.handleItemSelect} selectedItem={t3} processingItems={builder.processingItemIds} />;
    $[2] = builder.activeCategory;
    $[3] = builder.categoryItems;
    $[4] = builder.handleItemSelect;
    $[5] = builder.processingItemIds;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== builder) {
    t5 = () => builder.setShowSaveModal(false);
    $[10] = builder;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== builder.currentImageUrl || $[13] !== builder.currentOriginalImageUrl || $[14] !== builder.onSuccess || $[15] !== builder.selectedItems || $[16] !== builder.showSaveModal || $[17] !== t5) {
    t6 = <SaveOutfitModal isOpen={builder.showSaveModal} onClose={t5} selectedItems={builder.selectedItems} finalImageUrl={builder.currentImageUrl} originalImageUrl={builder.currentOriginalImageUrl} onSuccess={builder.onSuccess} />;
    $[12] = builder.currentImageUrl;
    $[13] = builder.currentOriginalImageUrl;
    $[14] = builder.onSuccess;
    $[15] = builder.selectedItems;
    $[16] = builder.showSaveModal;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t4 || $[20] !== t6) {
    t7 = <>{t4}{t6}</>;
    $[19] = t4;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}